/**
 * routeMeta.js - Registre central des routes pour le fil d'Ariane (breadcrumbs).
 *
 * Chaque entrée associe un *pattern* de route (au format react-router v6) à :
 *   - cle    : clé du libellé affiché dans le fil d'Ariane, traduit à la lecture
 *              (referentiels:routes.pages) et exposé en `label`
 *   - parent : pattern de la route parente, OU un nœud virtuel (préfixé par '#'),
 *              OU null pour la racine.
 *
 * Les nœuds virtuels (VIRTUAL_NODES) servent à grouper des pages sous une
 * rubrique qui n'a pas de page dédiée (ex. « Statistiques », « Reprise »).
 *
 * Le composant <Breadcrumbs/> remonte la chaîne `parent` pour reconstruire
 * le chemin complet. Aucune page n'a besoin d'être modifiée : il suffit
 * d'ajouter ici toute nouvelle route, et son libellé sous `routes.pages` dans
 * src/i18n/locales/{fr,en}/referentiels.json.
 */

import { avecLibelleTraduit } from '../i18n/libelles';

export const ROOT_PATTERN = '/dashboard';

/* Nœuds virtuels (rubriques sans page propre) ─────────────────────────────── */
export const VIRTUAL_NODES = avecLibelleTraduit({
  '#sinistres-grp': { parent: ROOT_PATTERN },
  '#finance-grp': { parent: ROOT_PATTERN },
  '#compta-grp': { parent: '#finance-grp' },
  '#docs-grp': { parent: ROOT_PATTERN },
  '#digital-grp': { parent: ROOT_PATTERN },
  '#stats-grp': { parent: ROOT_PATTERN },
  '#reprise-grp': { parent: ROOT_PATTERN },
  '#rbac-grp': { parent: ROOT_PATTERN },
  '#admin-grp': { parent: ROOT_PATTERN },
  '#param-grp': { parent: '#admin-grp' },
}, 'referentiels:routes.rubriques');

/* Registre des routes ─────────────────────────────────────────────────────── */
export const ROUTE_META = avecLibelleTraduit([
  /* Racine */
  { pattern: '/dashboard', cle: 'dashboard', parent: null },

  /* Sinistres */
  { pattern: '/sinistres', cle: 'sinistres', parent: '#sinistres-grp' },
  { pattern: '/sinistres/nouveau', cle: 'sinistresNouveau', parent: '/sinistres' },
  { pattern: '/sinistres/:id', cle: 'sinistresId', parent: '/sinistres' },
  { pattern: '/sinistres/:id/modifier', cle: 'sinistresIdModifier', parent: '/sinistres/:id' },
  { pattern: '/sinistres/:id/fiche', cle: 'sinistresIdFiche', parent: '/sinistres/:id' },
  { pattern: '/sinistres/:id/attestation', cle: 'sinistresIdAttestation', parent: '/sinistres/:id' },
  { pattern: '/sinistres/:id/non-garantie', cle: 'sinistresIdNonGarantie', parent: '/sinistres/:id' },
  { pattern: '/sinistres/:id/confirmation-garantie', cle: 'sinistresIdConfirmationGarantie', parent: '/sinistres/:id' },

  /* Canal mobile */
  { pattern: '/mobile/declarations', cle: 'mobileDeclarations', parent: '#sinistres-grp' },
  { pattern: '/mobile/rattachements', cle: 'mobileRattachements', parent: '#sinistres-grp' },
  { pattern: '/mobile/usagers', cle: 'mobileUsagers', parent: '#admin-grp' },

  /* PV */
  { pattern: '/pv-sinistres', cle: 'pvSinistres', parent: '#sinistres-grp' },
  { pattern: '/pv-sinistres/non-associes', cle: 'pvSinistresNonAssocies', parent: '/pv-sinistres' },

  /* Expertises */
  { pattern: '/expertises-medicales', cle: 'expertisesMedicales', parent: '#sinistres-grp' },

  /* Réclamations / Indemnisation */
  { pattern: '/reclamations', cle: 'reclamations', parent: '#sinistres-grp' },
  { pattern: '/reclamations/:id', cle: 'reclamationsId', parent: '/reclamations' },
  { pattern: '/indemnisation', cle: 'indemnisation', parent: '#sinistres-grp' },

  /* Délais / Relances / Bilatérales */
  { pattern: '/delais', cle: 'delais', parent: '#sinistres-grp' },
  { pattern: '/relances', cle: 'relances', parent: '#sinistres-grp' },
  { pattern: '/bilaterales', cle: 'bilaterales', parent: '#sinistres-grp' },
  { pattern: '/bilaterales/:id', cle: 'bilateralesId', parent: '/bilaterales' },

  /* Finance */
  { pattern: '/encaissements', cle: 'encaissements', parent: '#finance-grp' },
  { pattern: '/paiements', cle: 'paiements', parent: '#finance-grp' },
  { pattern: '/comptabilite/comptes', cle: 'comptabiliteComptes', parent: '#compta-grp' },
  { pattern: '/comptabilite/ecritures', cle: 'comptabiliteEcritures', parent: '#compta-grp' },
  { pattern: '/comptabilite/balance', cle: 'comptabiliteBalance', parent: '#compta-grp' },
  { pattern: '/comptabilite/fiscalite', cle: 'comptabiliteFiscalite', parent: '#compta-grp' },
  { pattern: '/comptabilite/parametrage', cle: 'comptabiliteParametrage', parent: '#compta-grp' },
  { pattern: '/lot-reglements', cle: 'lotReglements', parent: '#finance-grp' },
  { pattern: '/lot-reglements/:lotTrackingId', cle: 'lotReglementsLotTrackingId', parent: '/lot-reglements' },
  { pattern: '/reglements-multiple-experts', cle: 'reglementsMultipleExperts', parent: '#finance-grp' },

  /* Attestations & Docs */
  { pattern: '/attestations', cle: 'attestations', parent: '#docs-grp' },
  { pattern: '/attestations/commandes/:commandeId', cle: 'attestationsCommandesCommandeId', parent: '/attestations' },
  { pattern: '/courriers', cle: 'courriers', parent: '#docs-grp' },
  { pattern: '/courriers/bordereaux', cle: 'courriersBordereaux', parent: '/courriers' },
  { pattern: '/courriers/registre', cle: 'courriersRegistre', parent: '/courriers' },
  { pattern: '/messagerie', cle: 'messagerie', parent: '#docs-grp' },
  { pattern: '/ged', cle: 'ged', parent: '#docs-grp' },
  { pattern: '/ged/dossiers', cle: 'gedDossiers', parent: '/ged' },

  /* Digitalisation CB */
  { pattern: '/attestations-digitales', cle: 'attestationsDigitales', parent: '#digital-grp' },
  { pattern: '/attestations-digitales/commandes', cle: 'attestationsDigitalesCommandes', parent: '/attestations-digitales' },
  { pattern: '/attestations-digitales/:trackingId', cle: 'attestationsDigitalesTrackingId', parent: '/attestations-digitales' },
  { pattern: '/cles-api', cle: 'clesApi', parent: '#digital-grp' },
  { pattern: '/agents-verbalisateurs', cle: 'agentsVerbalisateurs', parent: '#digital-grp' },

  /* Statistiques */
  { pattern: '/stats/sinistres', cle: 'statsSinistres', parent: '#stats-grp' },
  { pattern: '/stats/financier', cle: 'statsFinancier', parent: '#stats-grp' },
  { pattern: '/stats/reporting', cle: 'statsReporting', parent: '#stats-grp' },
  { pattern: '/stats/cadence', cle: 'statsCadence', parent: '#stats-grp' },
  { pattern: '/stats/reclamations', cle: 'statsReclamations', parent: '#stats-grp' },
  { pattern: '/stats/graphique-enc-pai', cle: 'statsGraphiqueEncPai', parent: '#stats-grp' },
  { pattern: '/stats/reclamations-togo', cle: 'statsReclamationsTogo', parent: '#stats-grp' },
  { pattern: '/stats/bap-exercices', cle: 'statsBapExercices', parent: '#stats-grp' },
  { pattern: '/stats/litiges', cle: 'statsLitiges', parent: '#stats-grp' },
  { pattern: '/performance', cle: 'performance', parent: '#stats-grp' },

  /* Reprise historique */
  { pattern: '/reprise/sinistres', cle: 'repriseSinistres', parent: '#reprise-grp' },
  { pattern: '/reprise/encaissements', cle: 'repriseEncaissements', parent: '#reprise-grp' },
  { pattern: '/reprise/paiements', cle: 'reprisePaiements', parent: '#reprise-grp' },
  { pattern: '/reprise/etat-reclamations', cle: 'repriseEtatReclamations', parent: '#reprise-grp' },

  /* Profils & Habilitations */
  { pattern: '/profils', cle: 'profils', parent: '#rbac-grp' },
  { pattern: '/profils/new', cle: 'profilsNew', parent: '/profils' },
  { pattern: '/profils/:id/edit', cle: 'profilsIdEdit', parent: '/profils' },
  { pattern: '/habilitations', cle: 'habilitations', parent: '#rbac-grp' },
  { pattern: '/journal-activite', cle: 'journalActivite', parent: '#rbac-grp' },
  { pattern: '/securite/appareils', cle: 'securiteAppareils', parent: '#rbac-grp' },

  /* Administration */
  { pattern: '/organismes', cle: 'organismes', parent: '#admin-grp' },
  { pattern: '/utilisateurs', cle: 'utilisateurs', parent: '#rbac-grp' },
  { pattern: '/experts', cle: 'experts', parent: '#param-grp' },
  { pattern: '/messagerie/config', cle: 'messagerieConfig', parent: '#admin-grp' },
  { pattern: '/admin/reconciliation-imputations/:sinistreId', cle: 'adminReconciliationImputationsSinistreId', parent: '#admin-grp' },

  /* Référentiels / Paramètres métier */
  { pattern: '/parametres', cle: 'parametres', parent: '#admin-grp' },
  { pattern: '/parametres/pays', cle: 'parametresPays', parent: '#param-grp' },
  { pattern: '/parametres/entites-constat', cle: 'parametresEntitesConstat', parent: '#param-grp' },
  { pattern: '/parametres/pieces', cle: 'parametresPieces', parent: '#param-grp' },
  { pattern: '/parametres/listes', cle: 'parametresListes', parent: '#param-grp' },
  { pattern: '/parametres/banques', cle: 'parametresBanques', parent: '#param-grp' },
  { pattern: '/parametres/attestations', cle: 'parametresAttestations', parent: '#param-grp' },
  { pattern: '/parametres/baremes', cle: 'parametresBaremes', parent: '#param-grp' },

  /* Profil utilisateur */
  { pattern: '/mon-profil', cle: 'monProfil', parent: ROOT_PATTERN },
  { pattern: '/mon-profil/securite', cle: 'monProfilSecurite', parent: '/mon-profil' },
], 'referentiels:routes.pages', { cle: 'cle' });
